import React from 'react';
import clsx from 'clsx';

import { GiftPlan, GiftPlanInterval, useGiftPlansQuery } from '@edx/graphql/generated';
import { Caption, Title } from '@edx/components/typography';

type Props = {
  selectedPlan?: GiftPlan;
  onSelectPlan: (plan: GiftPlan) => void;
};

export const SubscriptionPlans: React.FC<Props> = ({ selectedPlan, onSelectPlan }) => {
  const { data } = useGiftPlansQuery();
  const [quarterlyPlan, yearlyPlan] = data?.giftPlans || [];

  return (
    <div className="flex flex-col md:flex-row md:space-x-4 md:space-y-0 space-x-0 space-y-4 w-full">
      <div
        className="flex-shrink group relative w-full md:w-1/2 bg-gray-800 overflow-hidden"
        onClick={(): void => onSelectPlan(quarterlyPlan)}
      >
        <picture>
          <source media="(min-width: 769px)" srcSet="/img/gifts/gifts_quarterly-medium.jpg" />
          <img
            className={clsx(
              'w-full transform transition duration-200',
              selectedPlan?.interval === GiftPlanInterval.Quarterly
                ? 'scale-105 filter-none'
                : 'group-hover:scale-105 filter-grayscale group-hover:filter-none'
            )}
            src="/img/gifts/gifts_quarterly-small.jpg"
            alt="Trimestral"
          />

          <div
            className={clsx(
              'absolute cursor-pointer inset-0 flex flex-row md:flex-col items-end md:items-stretch justify-between p-3 ',
              {
                'bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0':
                  selectedPlan?.interval !== GiftPlanInterval.Quarterly,
              }
            )}
          >
            <div className="flex flex-col text-gray-300 uppercase">
              <Caption tag="span" variant="semibold">
                Suscripción
              </Caption>
              <Title tag="span" className="md:text-large leading-tight">
                Trimestral
              </Title>
              <Title tag="span" size="small" className="leading-tight">
                {quarterlyPlan?.priceFormatted}
              </Title>
            </div>

            <div
              className={clsx(
                'transition duration-100 border-2 rounded text-gray-300 px-5 py-2 font-body font-medium text-xl text-center',
                selectedPlan?.interval === GiftPlanInterval.Quarterly
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300 border-opacity-50 group-hover:border-primary-500 group-hover:bg-primary-500'
              )}
            >
              Regala trimestral
            </div>
          </div>
        </picture>
      </div>

      <div
        className="flex-shrink group relative w-full md:w-1/2 bg-gray-800 overflow-hidden"
        onClick={(): void => onSelectPlan(yearlyPlan)}
      >
        <picture>
          <source media="(min-width: 769px)" srcSet="/img/gifts/gifts_yearly-medium.jpg" />
          <img
            className={clsx(
              'w-full transform transition duration-200',
              selectedPlan?.interval === GiftPlanInterval.Yearly
                ? 'scale-105 filter-none'
                : 'group-hover:scale-105 filter-grayscale group-hover:filter-none'
            )}
            src="/img/gifts/gifts_yearly-small.jpg"
            alt="Anual"
          />

          <div
            className={clsx(
              'absolute cursor-pointer inset-0 flex flex-row md:flex-col items-end md:items-stretch justify-between p-3 ',
              {
                'bg-gray-800 bg-opacity-50 group-hover:bg-opacity-0':
                  selectedPlan?.interval !== GiftPlanInterval.Yearly,
              }
            )}
          >
            <div className="flex flex-col text-gray-300 uppercase">
              <Caption tag="span" variant="semibold">
                Suscripción
              </Caption>
              <Title tag="span" className="md:text-large leading-tight">
                Anual
              </Title>
              <Title tag="span" size="small" className="leading-tight">
                {yearlyPlan?.priceFormatted}
              </Title>
            </div>

            <div
              className={clsx(
                'transition duration-100 border-2 rounded text-gray-300 px-5 py-2 font-body font-medium text-xl text-center',
                selectedPlan?.interval === GiftPlanInterval.Yearly
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300 border-opacity-50 group-hover:border-primary-500 group-hover:bg-primary-500'
              )}
            >
              Regala anual
            </div>
          </div>
        </picture>
      </div>
    </div>
  );
};
